import type { ActivityEvent, EventType } from '@/types/event';
import type { Agent } from '@/types/agent';
import type { Store } from '@/types/store';

let eventCounter = 1000;
function nextId() { return `ev_sim_${++eventCounter}`; }

const PUBLICATION_MSGS = [
  (agent: Agent, store: Store) => `${agent.codename} a publié 3 nouvelles fiches produit sur ${store.name}`,
  (agent: Agent, store: Store) => `${agent.codename} — optimisation SEO terminée pour ${store.name} · 8 pages mises à jour`,
  (agent: Agent, store: Store) => `${agent.codename} — publication Pinterest · 5 épingles pour ${store.name}`,
  (agent: Agent, store: Store) => `${agent.codename} a mis à jour les prix de ${store.name} · ${store.metrics.orders} commandes en cours`,
];

const SALE_MSGS = [
  (store: Store) => `Nouvelle vente — ${store.name} · ${(Math.random() * 30 + 8).toFixed(2)} €`,
  (store: Store) => `Commande reçue — ${store.name} · ${Math.floor(Math.random() * 3) + 1} article(s)`,
  (store: Store) => `${store.name} — achat confirmé · client récurrent`,
];

const SYSTEM_MSGS = [
  'Tick du moteur de simulation',
  'Sauvegarde d\'état automatique',
  'Synchronisation métriques en cours',
];

const SEO_MSGS = [
  (agent: Agent) => `${agent.codename} — nouveau ranking détecté · position gagnée sur 2 mots-clés`,
  (agent: Agent) => `${agent.codename} analyse les SERPs · rapport en cours de génération`,
];

const CUSTOMER_MSGS = [
  (agent: Agent) => `${agent.codename} a répondu à un avis client · ⭐⭐⭐⭐⭐`,
  (agent: Agent) => `${agent.codename} — ticket SAV résolu en ${Math.floor(Math.random() * 5) + 1} min`,
];

export function generateRandomEvent(
  agents: Agent[],
  stores: Store[]
): ActivityEvent | null {
  const onlineAgents = agents.filter(a => a.online);
  const activeStores = stores.filter(s => s.status === 'active');
  if (!onlineAgents.length || !activeStores.length) return null;

  const agent = onlineAgents[Math.floor(Math.random() * onlineAgents.length)];
  const store = activeStores[Math.floor(Math.random() * activeStores.length)];

  const roll = Math.random();
  let type: EventType;
  let message: string;

  if (roll < 0.25) {
    type = 'sale';
    message = SALE_MSGS[Math.floor(Math.random() * SALE_MSGS.length)](store);
  } else if (roll < 0.45) {
    type = 'publication';
    message = PUBLICATION_MSGS[Math.floor(Math.random() * PUBLICATION_MSGS.length)](agent, store);
  } else if (roll < 0.58) {
    type = 'seo';
    message = SEO_MSGS[Math.floor(Math.random() * SEO_MSGS.length)](agent);
  } else if (roll < 0.68) {
    type = 'customer';
    message = CUSTOMER_MSGS[Math.floor(Math.random() * CUSTOMER_MSGS.length)](agent);
  } else if (roll < 0.75) {
    type = 'collaboration';
    const other = onlineAgents.filter(a => a.id !== agent.id)[0];
    message = other
      ? `${agent.codename} collabore avec ${other.codename} sur ${store.name}`
      : `${agent.codename} démarre une analyse autonome`;
  } else {
    type = 'system';
    message = SYSTEM_MSGS[Math.floor(Math.random() * SYSTEM_MSGS.length)];
  }

  return {
    id: nextId(),
    type,
    agentId: agent.id,
    storeId: store.id,
    message,
    timestamp: Date.now(),
    read: false,
  };
}

export function generateValidationEvent(agent: Agent, taskTitle: string): ActivityEvent {
  return {
    id: nextId(),
    type: 'validation_needed',
    agentId: agent.id,
    storeId: null,
    message: `${agent.codename} demande une validation humaine — ${taskTitle}`,
    timestamp: Date.now(),
    read: false,
  };
}

export function generateMoveEvent(agent: Agent, roomName: string): ActivityEvent {
  return {
    id: nextId(),
    type: 'agent_moved',
    agentId: agent.id,
    storeId: null,
    message: `${agent.codename} se déplace vers ${roomName}`,
    timestamp: Date.now(),
    read: true, // pas d'alerte pour les déplacements
  };
}
